import { useEffect, useMemo, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import translations from './translations';

const KG_TO_LBS = 2.2046226218;

function toUnit(val, from, to) {
  const n = Number(val);
  if (!Number.isFinite(n)) return null;
  if (from === to) return n;
  if (from === 'kg' && to === 'lbs') return n * KG_TO_LBS;
  if (from === 'lbs' && to === 'kg') return n / KG_TO_LBS;
  return n;
}

function entryToKg(e) {
  if (!e) return null;
  if (typeof e.weightKg === 'number') return e.weightKg;
  if (typeof e.weight === 'number' && (e.unit === 'kg' || e.unit === 'lbs')) return toUnit(e.weight, e.unit, 'kg');
  const w = parseFloat(e.weight);
  if (!Number.isNaN(w) && (e.unit === 'kg' || e.unit === 'lbs')) return toUnit(w, e.unit, 'kg');
  return null;
}

function parseEnGbDate(dateStr) {
  const [dd, mm, yyyy] = String(dateStr || '').split('/').map((p) => parseInt(p, 10));
  if (!dd || !mm || !yyyy) return null;
  const d = new Date(yyyy, mm - 1, dd);
  return Number.isNaN(d.getTime()) ? null : d;
}

export default function BodyWeightTracker({ language, weightUnit, entries, goal, onAddEntry, onUpsertEntry, onDeleteEntry, onSaveGoal }) {
  const [weight, setWeight] = useState('');
  const [editingDate, setEditingDate] = useState(null);
  const [goalTypeInput, setGoalTypeInput] = useState(goal?.type || 'maintain');
  const [goalWeightInput, setGoalWeightInput] = useState('');
  const [isEditingGoal, setIsEditingGoal] = useState(false);

  const tt = translations[language] || translations.en;
  const t = {
    title: tt.bodyweight || (language === 'da' ? 'Kropsvægt' : 'Bodyweight'),
    subtitle: tt.bodyweightSubtitle,
    placeholder: weightUnit,
    save: tt.bodyweightSave || (language === 'da' ? 'Gem' : 'Save'),
    recent: tt.bodyweightRecent,
    empty: tt.bodyweightEmpty,
    goal: tt.bodyweightGoal,
    goalType: tt.bodyweightGoalType,
    goalWeight: tt.bodyweightGoalTarget,
    maintain: tt.bodyweightGoalMaintain,
    cut: tt.bodyweightGoalCut,
    bulk: tt.bodyweightGoalBulk,
    saveGoal: tt.bodyweightGoalSave,
  };

  const sorted = useMemo(() => {
    const arr = Array.isArray(entries) ? [...entries] : [];
    arr.sort((a, b) => {
      const da = parseEnGbDate(a.date);
      const db = parseEnGbDate(b.date);
      return (db?.getTime() || 0) - (da?.getTime() || 0);
    });
    return arr;
  }, [entries]);

  const chartData = useMemo(() => {
    return [...sorted]
      .slice()
      .reverse()
      .slice(-30)
      .map((e) => ({
        date: String(e.date || '').slice(0, 5),
        weight: (() => {
          const kg = entryToKg(e);
          return kg === null ? null : toUnit(kg, 'kg', weightUnit);
        })(),
      }));
  }, [sorted, weightUnit]);

  const latest = sorted[0];
  const prev = sorted[1];
  const latestInUnit = latest ? (() => {
    const kg = entryToKg(latest);
    return kg === null ? null : toUnit(kg, 'kg', weightUnit);
  })() : null;
  const prevInUnit = prev ? (() => {
    const kg = entryToKg(prev);
    return kg === null ? null : toUnit(kg, 'kg', weightUnit);
  })() : null;
  const delta = latestInUnit !== null && prevInUnit !== null ? (latestInUnit - prevInUnit) : null;

  const savedGoalMeta = useMemo(() => {
    if (!goal) return '—';
    const label = goal.type === 'cut' ? t.cut : goal.type === 'bulk' ? t.bulk : t.maintain;
    const targetInUnit = typeof goal.target === 'number'
      ? (goal.unit ? toUnit(goal.target, goal.unit, weightUnit) : goal.target)
      : null;
    if (typeof targetInUnit !== 'number' || !Number.isFinite(targetInUnit)) return `${label} · —`;
    const rounded = Math.round(targetInUnit * 10) / 10;
    const target = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
    return `${label} · ${target}${weightUnit}`;
  }, [goal, t.bulk, t.cut, t.maintain, weightUnit]);

  useEffect(() => {
    if (isEditingGoal) return;
    setGoalTypeInput(goal?.type || 'maintain');
    // Don't prefill the input after save/reload; keep the saved goal in the meta line.
    setGoalWeightInput('');
  }, [goal, weightUnit, isEditingGoal]);

  return (
    <div>
      <div style={{ marginBottom: '12px' }}>
        <p className="pr-card-title" style={{ marginBottom: '2px' }}>{t.title}</p>
        <p className="pr-card-subtitle">{t.subtitle}</p>
      </div>

      <div className="input-row" style={{ marginBottom: '12px' }}>
        <input
          className="input-field narrow"
          placeholder={t.placeholder}
          inputMode="decimal"
          value={weight}
          onChange={(e) => setWeight(e.target.value)}
        />
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => {
            const w = parseFloat(weight);
            if (Number.isNaN(w)) return;
            if (editingDate) {
              onUpsertEntry?.(editingDate, w);
            } else {
              onAddEntry(w);
            }
            setWeight('');
            setEditingDate(null);
          }}
        >
          {t.save}
        </button>
        {editingDate && (
          <button
            type="button"
            className="btn-icon"
            aria-label="Cancel edit"
            onClick={() => { setEditingDate(null); setWeight(''); }}
            title={editingDate}
          >
            ✕
          </button>
        )}
      </div>
      {editingDate && (
        <div className="pr-item-meta" style={{ marginTop: '-6px', marginBottom: '12px' }}>
          Editing: {editingDate}
        </div>
      )}

      <div className="pr-week" style={{ marginBottom: '12px' }}>
        <div className="pr-week-row">
          <div className="pr-week-title">{t.goal}</div>
          <div className="pr-week-meta">
            {savedGoalMeta}
          </div>
        </div>
        <div className="input-row">
          <select
            className="input-field wide"
            value={goalTypeInput}
            onChange={(e) => { setIsEditingGoal(true); setGoalTypeInput(e.target.value); }}
            style={{ maxWidth: 220 }}
          >
            <option value="maintain">{t.maintain}</option>
            <option value="cut">{t.cut}</option>
            <option value="bulk">{t.bulk}</option>
          </select>
          <input
            className="input-field narrow"
            placeholder={weightUnit}
            inputMode="decimal"
            value={goalWeightInput}
            onChange={(e) => { setIsEditingGoal(true); setGoalWeightInput(e.target.value); }}
          />
          <button
            type="button"
            className="btn btn-dark"
            onClick={() => {
              const w = parseFloat(goalWeightInput);
              if (Number.isNaN(w)) return;
              onSaveGoal({ type: goalTypeInput, target: w, unit: weightUnit });
              setGoalWeightInput('');
              setIsEditingGoal(false);
            }}
          >
            {t.saveGoal}
          </button>
        </div>
      </div>

      {latest && (
        <div className="pr-week" style={{ marginBottom: '12px' }}>
          <div className="pr-week-row">
            <div className="pr-week-title">{t.recent}</div>
            <div className="pr-week-meta">
              {latest.date} · {latestInUnit !== null ? latestInUnit.toFixed(1) : '—'}{weightUnit}
              {delta !== null ? ` (${delta > 0 ? '+' : ''}${delta.toFixed(1)}${weightUnit})` : ''}
            </div>
          </div>
          {chartData.length >= 2 && (
            <ResponsiveContainer width="100%" height={140}>
              <LineChart data={chartData}>
                <XAxis dataKey="date" tick={{ fill: 'var(--text-muted)', fontSize: 11 }} />
                <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 11 }} domain={['dataMin - 1', 'dataMax + 1']} />
                <Tooltip contentStyle={{ background: 'var(--surface-elevated)', border: '1px solid var(--border)', borderRadius: '8px' }} />
                <Line type="monotone" dataKey="weight" stroke="var(--accent)" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      )}

      <div className="pr-list">
        {sorted.slice(0, 8).map((e) => (
          <div
            key={`${e.date}-${e.weight}-${e.unit}`}
            className="pr-item"
            role="button"
            tabIndex={0}
            onClick={() => {
              const kg = entryToKg(e);
              const val = kg === null ? null : toUnit(kg, 'kg', weightUnit);
              if (val === null) return;
              setEditingDate(String(e.date || ''));
              setWeight(val.toFixed(1));
            }}
            onKeyDown={(ev) => {
              if (ev.key !== 'Enter') return;
              const kg = entryToKg(e);
              const val = kg === null ? null : toUnit(kg, 'kg', weightUnit);
              if (val === null) return;
              setEditingDate(String(e.date || ''));
              setWeight(val.toFixed(1));
            }}
          >
            <div className="pr-item-main">
              <div className="pr-item-exercise">{(() => {
                const kg = entryToKg(e);
                const val = kg === null ? null : toUnit(kg, 'kg', weightUnit);
                return `${val !== null ? val.toFixed(1) : '—'}${weightUnit}`;
              })()}</div>
              <div className="pr-item-meta">{e.date}</div>
            </div>
            {typeof onDeleteEntry === 'function' && (
              <button
                type="button"
                className="btn-icon pr-delete"
                aria-label="Delete entry"
                onClick={(ev) => {
                  ev.preventDefault();
                  ev.stopPropagation();
                  onDeleteEntry(String(e.date || ''));
                  if (editingDate === String(e.date || '')) {
                    setEditingDate(null);
                    setWeight('');
                  }
                }}
              >
                🗑
              </button>
            )}
          </div>
        ))}
        {(!sorted || sorted.length === 0) && (
          <div className="pr-empty">{t.empty}</div>
        )}
      </div>
    </div>
  );
}

